<template>
  <app-collapse class="mt-1">
    <app-collapse-item
      :is-visible="false"
      class="collapsible-section-bg"
      title="Persons submitting the declaration"
    >
      <template #header>
        <span
          class="lead collapse-title"
        >
          <section-status-icon
            :has-data="hasRepresentativeDeclarantData"
            :has-errors="hasSectionErrors"
          />
          Persons submitting the declaration
        </span>
      </template>
      <validation-observer>
        <!-- Representative and Declarant Section -->
        <div v-if="parcel.agent && parcel.declarant">
          <!-- Row 1: Representative fields -->
          <b-row class="representative-section">
            <b-col
              cols="12"
              md="3"
            >
              <b-form-group>
                <label>
                  Type of representation
                  <small class="text-muted">1306030000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Type of representation'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <div>{{ parcel.agent.type_of_representation }}</div>
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              md="3"
            >
              <b-form-group>
                <label>
                  Representative's identification number
                  <small class="text-muted">1306017000</small>
                </label>
                <div v-if="companyData">
                  {{ companyData.eori_number }}
                </div>
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              md="3"
            >
              <b-form-group>
                <label>
                  Representative's name
                  <small class="text-muted">1306016000</small>
                </label>
                <div v-if="companyData">
                  {{ companyData.name }}
                </div>
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              md="3"
            >
              <b-form-group>
                <label>
                  Agent number
                  <small class="text-muted">130600000A</small>
                </label>
                <div v-if="agentNumber">
                  {{ agentNumber }}
                </div>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Separator between Representative and Declarant -->
          <hr class="section-separator">

          <!-- Row 2: Submitter fields -->
          <b-row class="submitter-section">
            <b-col
              cols="12"
              md="3"
            >
              <b-form-group>
                <label>
                  Submitter's id
                </label>
                <div v-if="companyData">
                  {{ companyData.eori_number }}
                </div>
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              md="3"
            >
              <b-form-group>
                <label>
                  Submitter's name
                </label>
                <div v-if="companyData">
                  {{ companyData.name }}
                </div>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Separator between Submitter and Declarant -->
          <div class="section-separator" />

          <!-- Row 3: Declarant fields -->
          <b-row class="declarant-section">
            <b-col
              cols="12"
              md="3"
            >
              <b-form-group>
                <label>
                  Declarant's identification number
                  <small class="text-muted">1305017000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Declarant identification number'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <div v-if="companyData">
                  {{ companyData.eori_number }}
                </div>
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              md="3"
            >
              <b-form-group>
                <label>
                  Declarant's name
                  <small class="text-muted">1305016000</small>
                </label>
                <div v-if="companyData">
                  {{ companyData.name }}
                </div>
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              md="3"
            >
              <b-form-group>
                <label>
                  Country
                  <small class="text-muted">1305018020</small>
                </label>
                <div v-if="companyData">
                  {{ companyData.country }}
                </div>
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              md="3"
            >
              <b-form-group>
                <label>
                  Street and number
                  <small class="text-muted">1305018019</small>
                </label>
                <div v-if="companyData">
                  {{ companyData.street_and_number }}
                </div>
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              md="3"
            >
              <b-form-group>
                <label>
                  City
                </label>
                <div v-if="companyData">
                  {{ companyData.city }}
                </div>
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              md="3"
            >
              <b-form-group>
                <label>
                  Postcode
                  <small class="text-muted">1305018021</small>
                </label>
                <div v-if="companyData">
                  {{ companyData.postal_code }}
                </div>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Separator between Declarant and Contact person -->
          <hr class="section-separator">

          <!-- Row 3: Contact person fields -->
          <b-row class="contact-person-section">
            <b-col
              cols="12"
              md="4"
            >
              <b-form-group>
                <label>
                  Contact person's name
                  <small class="text-muted">1305074016</small>
                </label>
                <div v-if="contactPerson">
                  {{ contactPerson.name }}
                </div>
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              md="4"
            >
              <b-form-group>
                <label>
                  Contact person's phone number
                  <small class="text-muted">1305074075</small>
                </label>
                <div v-if="contactPerson">
                  {{ contactPerson.phone }}
                </div>
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              md="4"
            >
              <b-form-group>
                <label>
                  Contact person's e-mail address
                  <small class="text-muted">1305074076</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Contact person e-mail address'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <div v-if="contactPerson">
                  {{ contactPerson.email }}
                </div>
              </b-form-group>
            </b-col>
          </b-row>
        </div>
      </validation-observer>
    </app-collapse-item>
  </app-collapse>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import errorHighlightingMixin from '@/mixins/errorHighlightingMixin'
import SectionStatusIcon from '@/views/shipments/declarations/modals/components/h1/SectionStatusIcon.vue'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    ValidationObserver,
    AppCollapse,
    AppCollapseItem,
    SectionStatusIcon,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [errorHighlightingMixin],
  props: {
    parcel: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    agentNumber: {
      type: String,
      default: null,
    },
    contactPerson: {
      type: Object,
      default: null,
    },
    companyData: {
      type: Object,
      default: null,
    },
  },
  computed: {
    hasRepresentativeDeclarantData() {
      return !!(this.companyData || this.agentNumber || this.contactPerson)
    },
    hasSectionErrors() {
      return Object.keys(this.issuesMap).some(key => key.startsWith('representative.') || key.startsWith('declarant.') || key.startsWith('agent.'))
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/base/bootstrap-extended/variables';
@import '@core/scss/base/core/mixins/declarations-mixins';

.collapsible-section-bg {
  @include collapsible-section-bg;
}

.input-warning {
  @include input-warning;
}

.input-danger {
  @include input-danger;
}

/* Section separators for visual grouping */
.section-separator {
  margin: 1.5rem 0;
  border-top: 2px solid #d8dce0;
}

.representative-section {
  margin-bottom: 0;
}

.declarant-section {
  margin-bottom: 0;
}

.contact-person-section {
  margin-bottom: 0;
}
</style>
